import React, { useState, useRef, useEffect } from 'react'
import formatFullAddressObject from 'utils/address/formatAddress'
import { streamPropertyAgentChat } from 'app/AgentClients/propertyAgentClient'
import useVoiceConversation from 'utils/Voice/useVoiceConversation'
import VoiceControls, { VoiceStatus, isVoiceActive } from 'components/Voice/VoiceControls'
import MicPermissionModal from 'components/Voice/MicPermissionModal'
import { VOICE_NETWORK_ERROR_TITLE } from 'utils/Voice/VoiceConversationController'
import { getChatStrings, detectLang, DEFAULT_LANG } from './aiChatStrings'
import { isRedactedOwner } from 'utils/contactUtil'

// ── Keyframe styles (injected once) ───────────────────────────────────────────
const CHAT_KEYFRAMES = `
  @keyframes chatBounce {
    0%, 80%, 100% { transform: scale(0); opacity: 0.3; }
    40%           { transform: scale(1); opacity: 1; }
  }
  @keyframes psStarSpin {
    0%   { transform: rotate(0deg) scale(1); }
    25%  { transform: rotate(15deg) scale(1.3); }
    50%  { transform: rotate(0deg) scale(1); }
    75%  { transform: rotate(-15deg) scale(1.3); }
    100% { transform: rotate(0deg) scale(1); }
  }
`

// ── Anthropic API ─────────────────────────────────────────────────────────────
const TOOLS = [
  {
    name: 'update_rental_roi',
    description: 'Updates one or more inputs in the Rental ROI Calculator. Use when the user wants to model different financing, rent, or expense assumptions.',
    input_schema: {
      type: 'object',
      properties: {
        listingPrice:      { type: 'number', description: 'Purchase price in dollars' },
        downPaymentPct:    { type: 'number', description: 'Down payment as decimal (0.20 = 20%)' },
        interestRate:      { type: 'number', description: 'Annual interest rate as decimal (0.065 = 6.5%)' },
        mortgageYears:     { type: 'number', description: 'Loan term in years' },
        rentMonthly:       { type: 'number', description: 'Monthly rent in dollars' },
        otherRevMonthly:   { type: 'number', description: 'Other monthly revenue in dollars' },
        vacancyRate:       { type: 'number', description: 'Vacancy rate as decimal (0.05 = 5%)' },
        propertyTaxPct:    { type: 'number', description: 'Annual property tax as decimal of purchase price' },
        managementFeePct:  { type: 'number', description: 'Management fee as decimal of gross rent' },
        maintenanceResPct: { type: 'number', description: 'Maintenance reserve as decimal of gross rent' },
        insuranceYearly:   { type: 'number', description: 'Annual insurance cost in dollars' },
        utilitiesMonthly:  { type: 'number', description: 'Monthly utilities in dollars' },
        hoaYearly:         { type: 'number', description: 'Annual HOA in dollars' },
        appreciationPct:   { type: 'number', description: 'Annual appreciation rate as decimal (0.03 = 3%)' },
      },
      required: [],
    },
  },
  {
    name: 'update_fix_flip',
    description: 'Updates inputs in the Fix & Flip Analyzer. Use when the user wants to model a different purchase price, sale price, or holding period.',
    input_schema: {
      type: 'object',
      properties: {
        purchasePrice: { type: 'number', description: 'Purchase price in dollars' },
        holdingMonths: { type: 'number', description: 'Holding period in months' },
        salePrice:     { type: 'number', description: 'Target sale price in dollars' },
      },
      required: [],
    },
  },
  {
    name: 'open_comparables_tab',
    description: 'Opens the Comparables & Nearby Listings tab in the property details page so the user can view comparable sales. Use this after answering a question about comparables to offer the user a direct link to the full comps table.',
    input_schema: { type: 'object', properties: {}, required: [] },
  },
  {
    name: 'open_rental_roi_calculator',
    description: 'Opens the Rental ROI Calculator on the property details page. Use this whenever the user asks where the Rental ROI calculator is, how to open it, or wants to run rental return scenarios. Do NOT describe where to click — call this tool instead.',
    input_schema: { type: 'object', properties: {}, required: [] },
  },
  {
    name: 'open_fix_flip_calculator',
    description: 'Opens the Fix & Flip Analyzer on the property details page. Use whenever the user asks where the Fix & Flip calculator is, how to open it, or wants to model a flip. Do NOT describe where to click — call this tool instead.',
    input_schema: { type: 'object', properties: {}, required: [] },
  },
  {
    name: 'save_property',
    description: 'Saves the current property to the user\'s saved-properties list. ONLY use this tool when the user explicitly asks to save the property (e.g. "save this property", "add to my list", "save it"). Never suggest this proactively.',
    input_schema: { type: 'object', properties: {}, required: [] },
  },
  {
    name: 'skip_trace_property',
    description: 'Initiates a skip trace on the current property to find the owner\'s phone, email, and contact info. ONLY use this tool when the user explicitly asks to skip trace or get owner contact info (e.g. "skip trace this", "find the owner\'s phone", "get contact info"). Never suggest this proactively. The system will automatically save the property first if it hasn\'t been saved yet.',
    input_schema: { type: 'object', properties: {}, required: [] },
  },
]

// Plain text of a detail field value; links keep their text, anything else non-text is dropped.
function detailText(value) {
  if (value == null || typeof value === 'boolean') return null
  if (typeof value === 'string' || typeof value === 'number') return String(value)
  if (Array.isArray(value)) {
    const parts = value.map(detailText).filter(Boolean)
    return parts.length ? parts.join(' ') : null
  }
  if (React.isValidElement(value)) return detailText(value.props.children)
  return null
}

// Flattens the property detail tabs (Detail.js) into [{ name, fields: [{ label, value }] }] so the
// assistant sees every field the user sees (PROPS-3763). Grids are skipped.
function buildDetailSections(detailTabs) {
  const result = []
  const walk = (sections, prefix) => (sections || []).forEach(({ name, caption, fields }) => {
    const title = [prefix, caption, name].filter(Boolean).join(' — ')
    const rows = []
    ;(fields || []).forEach(({ name: label, value, tab, grid }) => {
      if (tab) walk(tab.sections, title)
      else if (!grid) {
        const text = detailText(value)
        if (text) rows.push({ label, value: text })
      }
    })
    if (rows.length) result.push({ name: title || 'Property Details', fields: rows })
  })
  Array.from(detailTabs || []).forEach((tab) => { walk(tab?.sections, tab?.name) })
  return result
}

// Same check the property details page uses to hide a redacted owner's contact info.
const isOwnerRedacted = (property) =>
  isRedactedOwner(property?.get('owner1FullName'), property?.get('owner2FullName'))

const isDncPhone = (item) => Boolean(item.dnc || item.publicDNC || item.myDNC)

// A value written into a markdown table cell: a `|` or a line break would end the cell or the row.
const tableCell = (value) => String(value ?? '').replace(/\|/g, '\\|').replace(/[\r\n]+/g, ' ')

// A skip trace result in the property details page's contact table layout (label, value, type, DNC),
// or null when the contact has no phone or email. Same limits as that table (5 phones, 2 emails),
// so the chat never lists more than the page (ATAI-508).
function skipTraceResultTable(contactInfo) {
  const phones = (contactInfo?.appendedPhones ?? []).slice(0, 5)
  const emails = (contactInfo?.appendedEmails ?? []).slice(0, 2)
  if (!phones.length && !emails.length) return null

  const lines = ['| Contact | Value | Type | DNC |', '|---|---|---|---|']
  phones.forEach((item, index) => {
    lines.push(`| Phone ${index + 1} | ${tableCell(item.phone)} | ${tableCell(item.type)} | ${isDncPhone(item) ? 'Do Not Call (DNC)' : '-'} |`)
  })
  emails.forEach((item, index) => {
    lines.push(`| Email ${index + 1} | ${tableCell(item.email)} | | |`)
  })
  return lines.join('\n')
}

function buildContext(property, rentalRef, flipRef, comparablesType, linkedProperties, isCompsTabActive, savedState, detailTabs) {
  const calculatorsOpen = !!(rentalRef?.current?.getInputs)

  const comps = comparablesType?.get?.('properties')?.toJS?.() ?? []
  const contactInfo = property.get('contact')?.toJS?.() ?? null
  const ownerRedacted = isOwnerRedacted(property)
  const phones = contactInfo?.appendedPhones ?? []

  return {
    property: {
      address: formatFullAddressObject(property.get('address'), true) || null,
      bedrooms: property.get('bedrooms') ?? null,
      bathrooms: property.get('bathrooms') ?? null,
      squareFeet: property.get('squareFeet') ?? null,
      lotSquareFeet: property.get('lotSquareFeet') ?? null,
      yearBuilt: property.get('yearBuilt') ?? null,
      landUse: property.get('landUse') ?? null,
      marketStatus: property.get('marketStatus') ?? null,
      distressStatus: property.get('distressStatus') ?? null,
      stateCode: property.get('address')?.get?.('stateCode') ?? null,
      apn: property.get('apn') ?? null,
      countyName: property.get('countyName') ?? null,
      vacant: property.get('vacant') ?? null,
      shortSale: property.get('shortSale') ?? null,
      hoa: property.get('hoa1Fee') ?? null,
      hoaPresent: property.get('hoaPresent') ?? null,
      hoas: [1, 2, 3, 4]
        .map((n) => ({
          name: property.get(`hoa${n}Name`) ?? null,
          type: property.get(`hoa${n}Type`) ?? null,
          fee: property.get(`hoa${n}Fee`) ?? null,
          feeFrequency: property.get(`hoa${n}FeeFrequency`) ?? null,
          feeType: property.get(`hoa${n}FeeType`) ?? null,
        }))
        .filter((h) => h.name || h.fee != null),
      purchaseMethod: property.get('purchaseMethod') ?? null,
      owner1FullName: property.get('owner1FullName') ?? null,
      owner2FullName: property.get('owner2FullName') ?? null,
      ownership: property.get('ownership') ?? null,
      ownerOccupancy: property.get('ownerOccupancy') ?? null,
      ownershipLength: property.get('ownershipLength') ?? null,
      estimatedValue: property.get('estimatedValue') ?? null,
      estimatedEquity: property.get('estimatedEquity') ?? null,
      equityPercentage: property.get('equityPercentage') ?? null,
      rentAmount: property.get('rentAmount') ?? null,
      grossYield: property.get('grossYield') ?? null,
      compSaleAmount: property.get('compSaleAmount') ?? null,
      compDaysOnMarket: property.get('compDaysOnMarket') ?? null,
      lastSale: property.get('lastSale')?.toJS?.() ?? null,
      activeLoans: property.get('activeLoans')?.toJS?.() ?? [],
      transactions: (property.get('transactions')?.toJS?.() ?? []).slice(0, 10),
      taxes: (property.get('taxes')?.toJS?.() ?? []).slice(0, 5),
      redacted: !!property.get('redacted'),
      propertySaved: !!savedState?.propertySaved,
      lastSkipTraceDate: property.get('lastSkipTraceDate') ?? null,
      skipTraceCreditsRemaining: savedState?.skipTraceCreditsRemaining ?? null,
      hasContactInfo: !!(
        contactInfo?.appendedPhones?.length ||
        contactInfo?.appendedEmails?.length
      ),

      contactId: contactInfo?.id ?? null,

      // Not sent at all for a redacted owner. Do Not Call numbers are left out so the assistant can
      // never suggest calling one (TCPA); only how many there are is sent.
      contactInfo: ownerRedacted ? null : {
        name: contactInfo?.name ?? null,

        // Shown as "(Litigator)" on the details page; the assistant must know it to avoid suggesting a call.
        litigator: Boolean(contactInfo?.litigatorFlag),

        phones: phones.filter((item) => !isDncPhone(item)).map((item) => ({
          phone: item.phone,
          type: item.type,
        })),

        dncPhoneCount: phones.filter(isDncPhone).length,

        emails: (contactInfo?.appendedEmails ?? []).map((item) => ({
          email: item.email,
        })),
      },
    },
    calculatorsOpen,
    isCompsTabActive: !!isCompsTabActive,
    detailSections: buildDetailSections(detailTabs),
    rentalRoi: {
      inputs: rentalRef?.current?.getInputs() ?? {},
      results: rentalRef?.current?.getResults() ?? {},
    },
    fixFlip: {
      inputs: flipRef?.current?.getInputs() ?? {},
      results: flipRef?.current?.getResults() ?? {},
    },
    comparables: comps.slice(0, 15).map((c) => ({
      streetAddress: c.streetAddress ?? null,
      saleDate: c.saleDate ?? null,
      saleAmount: c.saleAmount ?? null,
      status: c.status ?? null,
      pricePerSquareFoot: c.pricePerSquareFoot ?? null,
      bedrooms: c.bedrooms ?? null,
      bathrooms: c.bathrooms ?? null,
      squareFeet: c.squareFeet ?? null,
      distanceFromSubject: c.distanceFromSubject ?? null,
      daysOnMarket: c.daysOnMarket ?? null,
      source: c.type === 'U' ? 'Public Record' : 'MLS',
    })),
    linkedProperties: (linkedProperties?.toJS?.() ?? []).map((p) => ({
      streetAddress: p.streetAddress ?? null,
      countyName: p.countyName ?? null,
      bedrooms: p.bedrooms ?? null,
      bathrooms: p.bathrooms ?? null,
      squareFeet: p.squareFeet ?? null,
      lotSquareFeet: p.lotSquareFeet ?? null,
      landUse: p.landUse ?? null,
      owner1FullName: p.owner1FullName ?? null,
      ownershipLength: p.ownershipLength ?? null,
      openMortgageBalance: p.openMortgageBalance ?? null,
      estimatedValue: p.estimatedValue ?? null,
      vacant: p.vacant ?? null,
    })),
  }
}

const MAX_HISTORY_MESSAGES = 20
const STREAM_RENDER_INTERVAL_MS = 25 // ms between rendering each buffered chunk
const STREAM_CHARS_PER_TICK = 2 // characters to render per tick
const VOICE_COMPS_WAIT_MS = 20000 // longest a spoken comps question waits for comps to load

function trimHistory(history) {
  if (history.length <= MAX_HISTORY_MESSAGES) return history
  return history.slice(-MAX_HISTORY_MESSAGES)
}

function buildCachedTools() {
  return TOOLS.map((tool, i) =>
    i === TOOLS.length - 1
      ? { ...tool, cache_control: { type: 'ephemeral' } }
      : tool
  )
}

function callAnthropicStreaming(history, context, sessionId, { signal, onText, onToolUse, onDone, onError }) {
  const cachedTools = buildCachedTools()
  const trimmed = trimHistory(history)

  return streamPropertyAgentChat({
    history: trimmed,
    context,
    sessionId,
    tools: cachedTools,
    signal,
    onText,
    onToolUse,
    onDone,
    onError,
  })
}

function summarizeUpdates(toolName, updates, lang = DEFAULT_LANG) {
  const s = getChatStrings(lang)
  if (s.toolDescriptions[toolName]) return s.toolDescriptions[toolName]

  const L = s.fieldLabels
  const u = s.units
  const fmt = {
    listingPrice:      (v) => `${L.listingPrice} → $${Number(v).toLocaleString()}`,
    downPaymentPct:    (v) => `${L.downPaymentPct} → ${(v * 100).toFixed(1)}%`,
    interestRate:      (v) => `${L.interestRate} → ${(v * 100).toFixed(2)}%`,
    mortgageYears:     (v) => `${L.mortgageYears} → ${v} ${u.years}`,
    rentMonthly:       (v) => `${L.rentMonthly} → $${Number(v).toLocaleString()}`,
    otherRevMonthly:   (v) => `${L.otherRevMonthly} → $${v}${u.perMonth}`,
    vacancyRate:       (v) => `${L.vacancyRate} → ${(v * 100).toFixed(1)}%`,
    propertyTaxPct:    (v) => `${L.propertyTaxPct} → ${(v * 100).toFixed(2)}%`,
    managementFeePct:  (v) => `${L.managementFeePct} → ${(v * 100).toFixed(1)}%`,
    maintenanceResPct: (v) => `${L.maintenanceResPct} → ${(v * 100).toFixed(1)}%`,
    insuranceYearly:   (v) => `${L.insuranceYearly} → $${Number(v).toLocaleString()}${u.perYear}`,
    utilitiesMonthly:  (v) => `${L.utilitiesMonthly} → $${v}${u.perMonth}`,
    hoaYearly:         (v) => `${L.hoaYearly} → $${Number(v).toLocaleString()}${u.perYear}`,
    appreciationPct:   (v) => `${L.appreciationPct} → ${(v * 100).toFixed(1)}%${u.perYear}`,
    purchasePrice:     (v) => `${L.purchasePrice} → $${Number(v).toLocaleString()}`,
    holdingMonths:     (v) => `${L.holdingMonths} → ${v} ${u.months}`,
    salePrice:         (v) => `${L.salePrice} → $${Number(v).toLocaleString()}`,
  }
  return Object.entries(updates)
    .filter(([k]) => fmt[k])
    .map(([k, v]) => fmt[k](v))
    .join(', ') || s.updateFallback
}

// ── Lightweight markdown renderer ──────────────────────────────────────────────
function renderInline(text) {
  // **bold**, *italic*, `code`
  const parts = []
  const re = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`)/g
  let last = 0, m
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index))
    if (m[2]) parts.push(<strong key={m.index}>{m[2]}</strong>)
    else if (m[3]) parts.push(<em key={m.index}>{m[3]}</em>)
    else if (m[4]) parts.push(
      <code key={m.index} style={{
        background: '#f3f5f8', border: '1px solid #d0d2d5',
        borderRadius: 4, padding: '1px 5px', fontSize: '0.9em', fontFamily: 'monospace',
      }}>{m[4]}</code>
    )
    last = m.index + m[0].length
  }
  if (last < text.length) parts.push(text.slice(last))
  return parts
}

function MarkdownContent({ text }) {
  const lines = text.split('\n')
  const elements = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i]

    // Heading
    const hm = line.match(/^(#{1,3})\s+(.+)/)
    if (hm) {
      const level = hm[1].length
      const sizes = { 1: 15, 2: 14, 3: 13 }
      elements.push(
        <div key={i} style={{
          fontSize: sizes[level], fontWeight: 700,
          color: '#5B68C3',
          marginTop: level === 1 ? 10 : 8, marginBottom: 4,
          borderBottom: level === 1 ? '1px solid #d0d2d5' : 'none',
          paddingBottom: level === 1 ? 4 : 0,
        }}>
          {renderInline(hm[2])}
        </div>
      )
      i++; continue
    }

    // Horizontal rule
    if (/^---+$/.test(line.trim())) {
      elements.push(<hr key={i} style={{ border: 'none', borderTop: '1px solid #d0d2d5', margin: '8px 0' }} />)
      i++; continue
    }

    // Table — collect header + separator + rows
    if (line.startsWith('|') && i + 1 < lines.length && /^\|[-| :]+\|$/.test(lines[i + 1])) {
      const headers = line.split('|').filter((_, idx, arr) => idx > 0 && idx < arr.length - 1).map(h => h.trim())
      i += 2 // skip header + separator
      const rows = []
      while (i < lines.length && lines[i].startsWith('|')) {
        rows.push(lines[i].split('|').filter((_, idx, arr) => idx > 0 && idx < arr.length - 1).map(c => c.trim()))
        i++
      }
      elements.push(
        <div key={`tbl-${i}`} style={{ overflowX: 'auto', marginTop: 6, marginBottom: 6 }}>
          <table style={{
            width: '100%', borderCollapse: 'collapse', fontSize: 12,
            minWidth: 0,
          }}>
            <thead>
              <tr>
                {headers.map((h, hi) => (
                  <th key={hi} style={{
                    background: '#5B68C3', color: '#fff',
                    padding: '5px 8px', textAlign: 'left',
                    fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap',
                    borderRight: hi < headers.length - 1 ? '1px solid rgba(255,255,255,0.15)' : 'none',
                  }}>
                    {renderInline(h)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri} style={{ background: ri % 2 === 0 ? '#fff' : '#f3f5f8' }}>
                  {row.map((cell, ci) => (
                    <td key={ci} style={{
                      padding: '5px 8px', fontSize: 12,
                      color: '#3C4654',
                      border: '1px solid #d0d2d5',
                      borderTop: 'none',
                    }}>
                      {renderInline(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
      continue
    }

    // Bullet list item
    const bm = line.match(/^[-*]\s+(.+)/)
    if (bm) {
      elements.push(
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 2 }}>
          <span style={{ color: '#5B68C3', fontWeight: 700, flexShrink: 0, marginTop: 1 }}>•</span>
          <span style={{ fontSize: 13, color: '#3C4654', lineHeight: 1.5 }}>{renderInline(bm[1])}</span>
        </div>
      )
      i++; continue
    }

    // Blank line → spacing
    if (line.trim() === '') {
      if (elements.length > 0) elements.push(<div key={i} style={{ height: 6 }} />)
      i++; continue
    }

    // Regular paragraph line
    elements.push(
      <div key={i} style={{ fontSize: 13, color: '#3C4654', lineHeight: 1.6, marginBottom: 2 }}>
        {renderInline(line)}
      </div>
    )
    i++
  }

  return <div>{elements}</div>
}

// ── Sub-components ─────────────────────────────────────────────────────────────
function BotAvatar() {
  return (
    <div style={{ width: 24, height: 24, borderRadius: 6, background: '#5B68C3', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <svg width="14" height="14" viewBox="5 7 14 14" fill="none">
        <path d="M12.6563 7.4375L14.5313 11.4687L18.5625 13.3437C18.8125 13.4687 19 13.7187 19 14C19 14.3125 18.8125 14.5625 18.5625 14.6875L14.5313 16.5625L12.6563 20.5937C12.5313 20.8437 12.2813 21 12 21C11.6875 21 11.4375 20.8437 11.3125 20.5937L9.4375 16.5625L5.40625 14.6875C5.15625 14.5625 5 14.3125 5 14C5 13.7187 5.15625 13.4687 5.40625 13.3437L9.4375 11.4687L11.3125 7.4375C11.4375 7.1875 11.6875 7 12 7C12.2813 7 12.5313 7.1875 12.6563 7.4375Z" fill="white"/>
      </svg>
    </div>
  )
}

function LoadingDots() {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 8 }}>
      <div style={{ padding: '2px 6px 0 0', flexShrink: 0 }}>
        <BotAvatar />
      </div>
      <div style={{
        display: 'flex', gap: 4, padding: '10px 14px',
        background: '#fff', borderRadius: '12px 12px 12px 2px',
        boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.07)',
        border: '1px solid #DFE1E4',
      }}>
        {[0, 1, 2].map((i) => (
          <div key={i} style={{
            width: 6, height: 6, borderRadius: '50%', background: '#5B68C3',
            animation: 'chatBounce 1.2s ease-in-out infinite',
            animationDelay: `${i * 0.2}s`,
          }} />
        ))}
      </div>
    </div>
  )
}

function ToolActionCard({ msg, onApply }) {
  const s = getChatStrings(msg.lang)
  const labels = s.toolCards[msg.toolName] || s.toolCardFallback
  return (
    <div style={{
      background: '#fff', borderRadius: 10, marginBottom: 10,
      border: '1.5px solid #5B68C3',
      boxShadow: '0 2px 8px rgba(27,58,107,0.10)',
      overflow: 'hidden',
    }}>
      <div style={{
        background: '#5B68C3', padding: '6px 12px',
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <svg width="11" height="11" fill="none" stroke="#f16722" strokeWidth="2.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 11h.01M12 11h.01M15 11h.01M4 19V7a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2z"/>
        </svg>
        <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {labels.title}
        </span>
      </div>
      <div style={{ padding: '8px 12px 10px' }}>
        <div style={{ fontSize: 12, color: '#66707D', marginBottom: 8, lineHeight: 1.5 }}>
          {msg.description}
        </div>
        {msg.applied ? (
          <div style={{ fontSize: 12, color: '#10b981', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
            <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
            </svg>
            {labels.appliedText}
          </div>
        ) : (
          <button
            onClick={() => onApply(msg)}
            style={{
              background: '#f16722', color: '#fff', border: 'none',
              borderRadius: 6, padding: '5px 14px', cursor: 'pointer',
              fontSize: 11, fontWeight: 700, letterSpacing: '0.02em',
              display: 'flex', alignItems: 'center', gap: 4,
            }}
          >
            <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
            </svg>
            {labels.applyText}
          </button>
        )}
      </div>
    </div>
  )
}

function OpenPromptCard({ msg, onConfirm }) {
  const s = getChatStrings(msg.lang).openPrompt
  return (
    <div style={{
      background: '#fff', borderRadius: 10, marginBottom: 10,
      border: '1.5px solid #f16722',
      boxShadow: '0 2px 8px rgba(241,103,34,0.10)',
      overflow: 'hidden',
    }}>
      <div style={{ background: '#f16722', padding: '6px 12px' }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {s.title}
        </span>
      </div>
      <div style={{ padding: '8px 12px 10px' }}>
        <div style={{ fontSize: 12, color: '#66707D', marginBottom: 8, lineHeight: 1.5 }}>
          {s.before}<strong style={{ color: '#5B68C3' }}>{msg.calcLabel}</strong>{s.after}
        </div>
        <button
          onClick={() => onConfirm(msg)}
          style={{
            background: '#5B68C3', color: '#fff', border: 'none',
            borderRadius: 6, padding: '5px 14px', cursor: 'pointer',
            fontSize: 11, fontWeight: 700, letterSpacing: '0.02em',
          }}
        >
          {s.button}
        </button>
      </div>
    </div>
  )
}

function ViewCompsCard({ msg, onOpen }) {
  const s = getChatStrings(msg.lang).viewComps
  return (
    <div style={{
      background: '#fff', borderRadius: 10, marginBottom: 10,
      border: '1.5px solid #5B68C3',
      boxShadow: '0 2px 8px rgba(18,130,146,0.10)',
      overflow: 'hidden',
    }}>
      <div style={{ background: '#5B68C3', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <svg width="11" height="11" fill="none" stroke="#fff" strokeWidth="2.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"/>
        </svg>
        <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {s.title}
        </span>
      </div>
      <div style={{ padding: '8px 12px 10px' }}>
        <div style={{ fontSize: 12, color: '#66707D', marginBottom: 8, lineHeight: 1.5 }}>
          {msg.opened ? s.openedPrompt : s.prompt}
        </div>
        {msg.opened ? (
          <div style={{ fontSize: 12, color: '#5B68C3', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
            <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
            </svg>
            {s.done}
          </div>
        ) : (
          <button
            onClick={() => onOpen(msg)}
            style={{
              background: '#5B68C3', color: '#fff', border: 'none',
              borderRadius: 6, padding: '5px 14px', cursor: 'pointer',
              fontSize: 11, fontWeight: 700, letterSpacing: '0.02em',
              display: 'flex', alignItems: 'center', gap: 4,
            }}
          >
            <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
            </svg>
            {s.button}
          </button>
        )}
      </div>
    </div>
  )
}

// ── Clickable action card (open calculators) ───────────────────────────────────
// Button-first, opens on click, then shows a confirmation — mirrors ViewCompsCard.
// Skip trace / save are handled separately via ToolActionCard + handleApplyTool.
function ActionCard({ msg, onOpenAction }) {
  const s = getChatStrings(msg.lang)
  const cfg = s.actionCards[msg.action] || s.actionCardFallback

  return (
    <div style={{
      background: '#fff', borderRadius: 10, marginBottom: 10,
      border: '1.5px solid #5B68C3',
      boxShadow: '0 2px 8px rgba(18,130,146,0.10)',
      overflow: 'hidden',
    }}>
      <div style={{ background: '#5B68C3', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {cfg.label}
        </span>
      </div>
      <div style={{ padding: '8px 12px 10px' }}>
        <div style={{ fontSize: 12, color: '#66707D', marginBottom: 8, lineHeight: 1.5 }}>
          {msg.opened ? s.actionCardOpened(cfg.label) : cfg.prompt}
        </div>
        {msg.opened ? (
          <div style={{ fontSize: 12, color: '#5B68C3', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
            <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
            </svg>
            {cfg.done}
          </div>
        ) : (
          <button
            onClick={() => onOpenAction(msg)}
            style={{
              background: '#5B68C3', color: '#fff', border: 'none',
              borderRadius: 6, padding: '5px 14px', cursor: 'pointer',
              fontSize: 11, fontWeight: 700, letterSpacing: '0.02em',
              display: 'flex', alignItems: 'center', gap: 4,
            }}
          >
            <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
            </svg>
            {cfg.button}
          </button>
        )}
      </div>
    </div>
  )
}

function MessageItem({ msg, onApply, onConfirmOpen, onOpenComps, onOpenAction }) {
  if (msg.role === 'tool_action') return <ToolActionCard msg={msg} onApply={onApply} />
  if (msg.role === 'open_prompt') return <OpenPromptCard msg={msg} onConfirm={onConfirmOpen} />
  if (msg.role === 'view_comps') return <ViewCompsCard msg={msg} onOpen={onOpenComps} />
  if (msg.role === 'action') return <ActionCard msg={msg} onOpenAction={onOpenAction} />

  const isUser = msg.role === 'user'
  return (
    <div style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
      {!isUser && (
        <div style={{ padding: '2px 6px 0 0', flexShrink: 0 }}>
          <BotAvatar />
        </div>
      )}
      <div style={{
        maxWidth: '82%',
        background: isUser ? '#5B68C3' : '#fff',
        color: isUser ? '#fff' : '#3C4654',
        borderRadius: isUser ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
        padding: '8px 12px',
        fontSize: 13,
        fontFamily: 'Inter, sans-serif',
        lineHeight: 1.6,
        boxShadow: isUser ? 'none' : '0px 1px 3px 0px rgba(0, 0, 0, 0.07)',
        border: isUser ? 'none' : '1px solid #DFE1E4',
        whiteSpace: isUser ? 'pre-wrap' : undefined,
      }}>
        {isUser ? msg.content : <MarkdownContent text={msg.content} />}
      </div>
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────
function buildWelcomeMessage(property) {
  const address = formatFullAddressObject(property.get('address'), true) || 'this property'
  const estValue = property.get('estimatedValue')
  const equityPct = property.get('equityPercentage')
  const rentAmt = property.get('rentAmount')
  const beds = property.get('bedrooms')
  const baths = property.get('bathrooms')
  const sqft = property.get('squareFeet')
  const roiR = property.get('rentAmount')

  const dlr = (v) => v != null ? `~$${Number(v).toLocaleString()}` : null
  const pctFmt = (v) => v != null ? `${Math.round(v)}%` : null

  let msg = `Welcome to PropStream Intelligence! I'm here to help you analyze **${address}**`
  const propDesc = [beds && `${beds} bed`, baths && `${baths} bath`, sqft && `${Number(sqft).toLocaleString()} sqft`].filter(Boolean).join(', ')
  if (propDesc) msg += ` (${propDesc})`
  msg += '.'

  msg += '\n\nI have full access to this property\'s data, including:'
  if (estValue) msg += `\n- 📊 **Valuations & Equity** (${dlr(estValue)} estimated value${equityPct ? `, ${pctFmt(equityPct)} equity` : ''})`
  msg += `\n- 💰 **Rental ROI Calculator**${roiR ? ` (currently showing ${dlr(rentAmt)}/mo cash flow)` : ''}`
  msg += '\n- 🔧 **Fix & Flip Analyzer**'
  msg += '\n- 🏠 **Loan & Transaction History**'
  msg += '\n- 📋 **Comparable Sales**'

  msg += '\n\nWhat would you like to explore? For example:'
  msg += '\n- *"Is this a good rental investment?"*'
  msg += '\n- *"What if I put 30% down?"*'
  msg += '\n- *"Show me the comparables"*'
  msg += '\n- *"Walk me through the fix & flip numbers"*'

  return msg
}

const DISPLAY_PROMPT = "Give me a summary of this property";

const AI_SUMMARY_PROMPT = "Generate a summary for the currently viewed property using EXACTLY this structure and section order, every time, regardless of which data is available — do not add, remove, reorder, or rename sections, and do not substitute your own headings or a different paragraph structure:\n\n**Overview** — one sentence on property type, beds/baths, sqft; bold the subject property's address or the most identifying property phrase using **...**.\n**Valuation & ARV** — estimated value and After Repair Value; bold key valuation numbers such as the estimated value, ARV, assessed value, or important comp-derived values.\n**Comparables** — comps summary, or \"Not available\" if none are available; bold important comparable metrics such as average/median sale price, price per sqft, or estimated equity percentage when relevant.\n**Distress Indicators** — distress status, vacancy, short sale, or \"None found\" if none; if a meaningful distress status is present (especially short sale, vacancy, or foreclosure/distress status), bold that status wording — do not invent a status if none exists.\n**Mortgage Information** — active loans, or \"None on record\" if none; bold important mortgage status or totals such as \"free and clear\", a significant active loan count, combined estimated loan balance, or total in loans.\n**Linked Properties** — owner's other properties, or \"None found\" if none; bold key owner portfolio details such as the number of linked properties, total portfolio value, total loans, and total equity.\n**Suggested Next Steps** — a bulleted list of exactly these four items, in this exact order, each rendered with its exact icon: \"📊 Open the Rental ROI Calculator\", \"🔨 Open the Fix & Flip Calculator\", \"🏡 Run Comps\", \"🔍 Skip Trace the Property\". Preserve these exact icons — do not omit, replace, or reorder them.\n\nFormatting rules for bolded values: in every section above (except Suggested Next Steps), use markdown **...** to bold only the specific property-related facts and values called out for that section — never bold an entire sentence, only the key number, status, or identifying phrase within it. Apply this bolding consistently across every property summary you generate, regardless of which values are actually available.\n\nEvery section must always appear, even when its data is unavailable — use a consistent fallback such as \"Not available\", \"None found\", or \"None on record\" (as appropriate to the section) instead of omitting it. Format every section label as bold text (e.g. **Overview**), never as a markdown heading (#) or an arbitrary label. Aim for approximately 850–900 characters including spaces when possible, but prioritize following the exact section structure above over hitting that count precisely. Return only the final summary, with no preamble."

export default function PropertyAIChat({ open,
  setOpen, property, rentalRef, flipRef, calculatorsOpen, onOpenCalculators, onCloseCalculators, comparablesType, onOpenCompsTab, isCompsTabActive, loadComparables, linkedProperties,propertySaved, onSaveProperty, onSkipTrace, skipTraceCreditsRemaining, runAISummary, onAISummaryComplete, detailTabs }) {
  const [messages, setMessages]           = useState(() => [{
    id: 'welcome',
    role: 'assistant',
    content: buildWelcomeMessage(property),
  }])
  const [history, setHistory]             = useState([])
  const [inputText, setInputText]         = useState('')
  const [loading, setLoading]             = useState(false)
  const [streaming, setStreaming]         = useState(false)
  const [pendingToolAction, setPendingToolAction] = useState(null)
  const [pendingCompsQuestion, setPendingCompsQuestion] = useState(null)
  const messagesEndRef                     = useRef(null)
  const inputRef                           = useRef(null)
  const prevPropertyId                     = useRef(property?.get('id'))
  const sessionIdRef                       = useRef(`property-${property?.get('id') || 'unknown'}-${Date.now()}`)
  // Aborts the in-flight stream on unmount or when the user hits Stop.
  const abortRef                           = useRef(null)
  // Active text-drip interval, cleared on unmount so it can't setState after teardown.
  const renderTimerRef                     = useRef(null)
  // Guards async stream callbacks against firing after the component unmounts.
  const mountedRef                         = useRef(true)
  // Language of the ongoing conversation (EN / ES), detected from the user's
  // messages. Frontend-rendered confirmations and cards use it so they match
  // the language the model is replying in (PROPS-3320).
  const langRef                            = useRef(DEFAULT_LANG)

  // Cancel any in-flight stream / drip loop when the component goes away.
  useEffect(() => () => {
    mountedRef.current = false
    abortRef.current?.abort()
    if (renderTimerRef.current) clearInterval(renderTimerRef.current)
  }, [])

  // ── Voice (ATAI-324) ──
  // Same voice engine as AI Search. A reply is read aloud only when its
  // question was spoken; voiceGenRef moves on whenever voice is stopped
  // (chat closed, property changed, End, a typed/AI Summary send), so a reply
  // that finishes afterwards is never spoken.
  const voiceGenRef                        = useRef(0)
  const handleSendRef                      = useRef(null)
  const voice = useVoiceConversation({
    stripMarkup: true,
    onSubmit: (text) => handleSendRef.current?.(text, { voice: true }),
  })
  const voiceActive = isVoiceActive(voice)
  const voiceBusy = loading || !!pendingCompsQuestion
  // Leaving voice from outside the voice controls (chat closed, property
  // changed, a typed/AI Summary send) ends it fully, mutes included: once voice
  // mode is off the mute buttons are gone, so a mute left on would disable the
  // mic button with no way to undo it.
  // Leaving voice also drops a spoken comps question still waiting for comps,
  // so the mic isn't held busy (and no late answer or notice turns up) after
  // the user has moved on. A typed comps question keeps waiting as before.
  const dropVoiceCompsWait = () =>
    setPendingCompsQuestion((cur) => (cur?.voiceTurn ? null : cur))
  const stopVoice = () => {
    voiceGenRef.current += 1
    voice.end()
    dropVoiceCompsWait()
  }
  const handleVoiceEnd = () => {
    voiceGenRef.current += 1
    voice.end()
    dropVoiceCompsWait()
    setInputText('') // End restores an empty box, as in AI Search
  }
  const speakVoiceReply = (text, gen) => {
    if (text && gen === voiceGenRef.current) voice.speak(text)
  }
  // Latest property, for callbacks that run after a save (their own `property` is from the click).
  const propertyRef = useRef(property)
  propertyRef.current = property
  // Set only once a skip trace the chat asked for has actually started: the property's
  // lastSkipTraceDate at that moment, and `finished` once its contact has been reloaded. The trace is
  // done when the reloaded contact brings a different date or `finished` is set (a re-run on an already
  // traced contact keeps the same date, ATAI-511), then the watch ends (null = not watching).
  const skipTraceWatchRef = useRef(null)
  // A skip trace the chat asked for right after saving the property: started by the effect below
  // once the saved state and the reloaded contact have reached a render (null = none waiting).
  const pendingSkipTraceRef = useRef(null)

  // ── Drag & resize state ──
  const DEFAULT_POS = { right: 20, bottom: 0 }
  const DEFAULT_SIZE = { width: 390, height: 540 }
  const MIN_SIZE = { width: 320, height: 400 }
  const [panelPos, setPanelPos] = useState(DEFAULT_POS)
  const [panelSize, setPanelSize] = useState(DEFAULT_SIZE)

  const handleDragStart = (e) => {
    if (e.target.closest('button') || e.target.closest('input')) return
    e.preventDefault()
    const startX = e.clientX
    const startY = e.clientY
    const startRight = panelPos.right
    const startBottom = panelPos.bottom
    const curW = panelSize.width
    const curH = panelSize.height
    const onMove = (ev) => {
      const dx = ev.clientX - startX
      const dy = ev.clientY - startY
      const vw = window.innerWidth
      const vh = window.innerHeight
      setPanelPos({
        right: Math.min(vw - curW, Math.max(0, startRight - dx)),
        bottom: Math.min(vh - curH, Math.max(0, startBottom - dy)),
      })
    }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  const handleResizeStart = (e) => {
    e.preventDefault()
    e.stopPropagation()
    const startX = e.clientX
    const startY = e.clientY
    const startW = panelSize.width
    const startH = panelSize.height
    const startRight = panelPos.right
    const startBottom = panelPos.bottom
    const onMove = (ev) => {
      const dx = ev.clientX - startX
      const dy = ev.clientY - startY
      const vw = window.innerWidth
      const vh = window.innerHeight
      // Max size: can't exceed viewport minus current position
      const maxW = vw - startRight
      const maxH = vh - startBottom
      const newW = Math.min(maxW, Math.max(MIN_SIZE.width, startW - dx))
      const newH = Math.min(maxH, Math.max(MIN_SIZE.height, startH - dy))
      setPanelSize({ width: newW, height: newH })
      setPanelPos(prev => ({ ...prev, right: startRight }))
    }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  // Reset chat when property changes
  useEffect(() => {
    const currentId = property?.get('id')
    if (currentId && currentId !== prevPropertyId.current) {
      prevPropertyId.current = currentId
      sessionIdRef.current = `property-${currentId}-${Date.now()}`
      // Cancel work still in flight for the previous property so its late
      // stream text, cards or calculator updates can't land in the reset chat.
      abortRef.current?.abort()
      if (renderTimerRef.current) {
        clearInterval(renderTimerRef.current)
        renderTimerRef.current = null
      }
      setPendingToolAction(null)
      setLoading(false)
      setStreaming(false)
      setMessages([{
        id: 'welcome-' + currentId,
        role: 'assistant',
        content: buildWelcomeMessage(property),
      }])
      setHistory([])
      langRef.current = DEFAULT_LANG
      setInputText('')
      // A comps question from the previous property must not be answered (or
      // time out) in this property's chat
      setPendingCompsQuestion(null)
      setOpen(false)
      stopVoice()
      skipTraceWatchRef.current = null
      pendingSkipTraceRef.current = null
    }
  }, [property])

  // Closing the chat stops listening and speaking. Only on an actual close:
  // stopping on first mount would cancel the browser's (global) speech, which
  // could cut off an AI Search reply still being read aloud.
  const wasOpenRef = useRef(open)
  useEffect(() => {
    if (wasOpenRef.current && !open) stopVoice()
    wasOpenRef.current = open
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  // A spoken comps question can't hold voice mode forever: if the comps never
  // arrive (no comps search for this property, or an empty/failed load), give
  // up, say so, and let listening resume. Typed comps questions keep their
  // existing behaviour.
  useEffect(() => {
    if (!pendingCompsQuestion?.voiceTurn) return undefined
    const { voiceGen, lang = langRef.current } = pendingCompsQuestion
    const timer = setTimeout(() => {
      const notice = getChatStrings(lang).compsLoadFailed
      setPendingCompsQuestion(null)
      setMessages((prev) => [...prev, {
        id: Date.now() + '_comps_timeout',
        role: 'assistant',
        content: notice,
      }])
      speakVoiceReply(notice, voiceGen)
    }, VOICE_COMPS_WAIT_MS)
    return () => clearTimeout(timer)
  }, [pendingCompsQuestion]) // eslint-disable-line react-hooks/exhaustive-deps

  // Listening never runs while a reply (or a comps load) is in flight, and
  // resumes on its own once it's done — same as AI Search.
  useEffect(() => {
    voice.setBusy(voiceBusy)
    if (!voiceBusy) voice.restartIfReady()
  }, [voiceBusy]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Watches the contact data rather than the skipTraceCompleted prop: a trace started through
    // Add to Group's "Skip Trace This Property" checkbox (withProperty.js) never sets that prop,
    // but both flows reload the contact through getPropertyContact once the trace finishes.
    const watch = skipTraceWatchRef.current;
    if (!watch) return;

    // update_contact_append_counts only moves contact_append_date when the property's count of traced
    // contacts changes, so a re-run on the same contact keeps the old date: `finished` covers that.
    const lastSkipTraceDate = property?.get('lastSkipTraceDate') ?? null;
    const dateChanged = !!lastSkipTraceDate && lastSkipTraceDate !== watch.lastSkipTraceDate;
    if (!dateChanged && !watch.finished) return;
    skipTraceWatchRef.current = null;

    // Never shown for a redacted owner (the same rule buildContext uses).
    if (isOwnerRedacted(property)) return;

    const table = skipTraceResultTable(property?.get('contact')?.toJS?.());
    const strings = getChatStrings(langRef.current);
    let content = strings.skipTraceNoResults;
    if (table) {
      // A duplicate (traced in the last 6 months) ran no new search: these are the cached results
      content = watch.duplicate
        ? `${strings.skipTraceAlreadyDone}\n\n${table}`
        : `Skip trace complete — here's what we found:\n\n${table}`;
    }

    setMessages((prev) => [
      ...prev,
      {
        id: Date.now() + '_skip_trace_result',
        role: 'assistant',
        content,
      },
    ]);
  }, [property]);

  // Starts a skip trace the chat asked for right after a save, once this render has the saved state
  // and the reloaded contact. Detail.handleSkipTrace reads its own props, which are only current by now.
  useEffect(() => {
    const start = pendingSkipTraceRef.current;
    if (!start || !propertySaved || !property?.getIn(['contact', 'id'])) return;
    pendingSkipTraceRef.current = null;
    start();
  }, [property, propertySaved]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  useEffect(() => {
    if (open) setTimeout(() => {
      const el = inputRef.current
      if (el) {
        el.focus()
        const len = el.value.length
        el.setSelectionRange(len, len)
      }
    }, 100)
  }, [open])

   useEffect(() => {
    if (!open || !runAISummary) return;

    handleSend(AI_SUMMARY_PROMPT);

    onAISummaryComplete?.();
  }, [open, runAISummary]);

  // When comparables data arrives after auto-load, answer the pending question
  useEffect(() => {
    const comps = comparablesType?.get?.('properties')?.toJS?.() ?? []
    if (comps.length === 0 || !pendingCompsQuestion) return

    // Use the language of the question that triggered the load, not whatever
    // the user typed while comps were loading.
    const { pendingHistory, voiceTurn, voiceGen, lang = langRef.current } = pendingCompsQuestion
    setPendingCompsQuestion(null)
    setLoading(true)

    const context = buildContext(property, rentalRef, flipRef, comparablesType, linkedProperties, isCompsTabActive, { propertySaved, skipTraceCreditsRemaining }, detailTabs)
    const streamMsgId = Date.now() + '_comps_stream' + Math.random()
    let fullText = ''
    let renderedLength = 0
    let renderTimer = null
    let streamDone = false
    let errorText = ''

    function startRenderLoop() {
      if (renderTimer) return
      renderTimer = setInterval(() => {
        if (renderedLength < fullText.length) {
          renderedLength = Math.min(renderedLength + STREAM_CHARS_PER_TICK, fullText.length)
          setMessages((prev) => prev.map((m) =>
            m.id === streamMsgId ? { ...m, content: fullText.slice(0, renderedLength) } : m
          ))
        } else if (streamDone) {
          clearInterval(renderTimer)
          if (renderTimerRef.current === renderTimer) renderTimerRef.current = null
          renderTimer = null
        }
      }, STREAM_RENDER_INTERVAL_MS)
      renderTimerRef.current = renderTimer
    }

    setMessages((prev) => [...prev, {
      id: streamMsgId,
      role: 'assistant',
      content: '',
    }])

    // A later message may still be streaming (input stays enabled while comps
    // load). Aborting it makes it stale, so its own cleanup won't run — stop
    // its drip timer and reset `streaming` here before taking ownership.
    abortRef.current?.abort()
    if (renderTimerRef.current) {
      clearInterval(renderTimerRef.current)
      renderTimerRef.current = null
    }
    setStreaming(false)
    abortRef.current = new AbortController()
    // Stale once this request is aborted (Stop, property change, newer request)
    // or the component unmounts — late callbacks must not touch the chat.
    const controller = abortRef.current
    const isStale = () => !mountedRef.current || controller.signal.aborted

    callAnthropicStreaming(pendingHistory, context, sessionIdRef.current, {
      signal: abortRef.current.signal,
      onText: (text) => {
        if (isStale()) return
        fullText += text
        startRenderLoop()
      },
      onToolUse: (toolName, args) => {
        if (isStale()) return
        if (toolName === 'open_comparables_tab') {
          if (!isCompsTabActive) {
            setMessages((prev) => [...prev, {
              id: Date.now() + '_vc' + Math.random(),
              role: 'view_comps',
              lang,
              opened: false,
            }])
          }
        } else if (
          toolName === 'open_rental_roi_calculator' ||
          toolName === 'open_fix_flip_calculator'
        ) {
          setMessages((prev) => [...prev, {
            id: Date.now() + '_ac' + Math.random(),
            role: 'action',
            lang,
            action: toolName,
            opened: false,
          }])
        } else {
          setMessages((prev) => [...prev, {
            id: Date.now() + '_t' + Math.random(),
            role: 'tool_action',
            lang,
            toolName,
            updates: args || {},
            description: summarizeUpdates(toolName, args || {}, lang),
            applied: false,
          }])
        }
      },
      onDone: () => {},
      onError: (err) => {
        if (isStale()) return
        errorText = getChatStrings(lang).error(err.message)
        setMessages((prev) => [...prev, {
          id: Date.now() + '_err',
          role: 'assistant',
          content: errorText,
        }])
      },
    })
      .then(async () => {
        streamDone = true
        await new Promise((resolve) => {
          const check = setInterval(() => {
            if (renderedLength >= fullText.length || isStale()) {
              clearInterval(check)
              resolve()
            }
          }, STREAM_RENDER_INTERVAL_MS)
        })
        if (isStale()) return
        if (!fullText) {
          setMessages((prev) => prev.filter((m) => m.id !== streamMsgId))
        }
        if (voiceTurn) speakVoiceReply(fullText || errorText, voiceGen)
        const updatedHistory = fullText
          ? [...pendingHistory, { role: 'assistant', content: fullText }]
          : [...pendingHistory]
        setHistory(updatedHistory)
      })
      .catch(async (err) => {
        streamDone = true
        if (fullText) {
          await new Promise((resolve) => {
            const check = setInterval(() => {
              if (renderedLength >= fullText.length || isStale()) {
                clearInterval(check)
                resolve()
              }
            }, STREAM_RENDER_INTERVAL_MS)
          })
        }
        if (renderTimer) { clearInterval(renderTimer); renderTimer = null }
        if (isStale() || err.name === 'AbortError') return
        setMessages((prev) => prev.filter((m) => m.id === streamMsgId && !m.content ? false : true))
        setMessages((prev) => [...prev, {
          id: Date.now() + '_err',
          role: 'assistant',
          content: getChatStrings(lang).error(err.message),
        }])
        if (voiceTurn) speakVoiceReply(getChatStrings(lang).error(err.message), voiceGen)
      })
      .finally(() => { if (!isStale()) setLoading(false) })
  }, [comparablesType]) // eslint-disable-line react-hooks/exhaustive-deps

  // Apply pending tool action once calculators are open and refs are mounted
  useEffect(() => {
    if (!calculatorsOpen || !pendingToolAction) return
    const timer = setTimeout(() => {
      const { toolName, updates, id } = pendingToolAction
      const s = getChatStrings(pendingToolAction.lang || langRef.current)
      const ref = toolName === 'update_rental_roi' ? rentalRef : flipRef
      const calcLabel = s.calcLabels[toolName] || s.calcLabels.update_fix_flip
      if (ref?.current?.setInputs) {
        ref.current.setInputs(updates)
        setMessages((prev) => prev.map((m) => m.id === id ? { ...m, applied: true } : m))
        setMessages((prev) => [...prev, {
          id: Date.now() + '_confirm',
          role: 'assistant',
          content: s.calcUpdated(calcLabel),
        }])
        setPendingToolAction(null)
      }
    }, 250)
    return () => clearTimeout(timer)
  }, [calculatorsOpen, pendingToolAction])

  function handleApplyTool(toolActionMsg) {
    const { toolName, updates } = toolActionMsg
    const lang = toolActionMsg.lang || langRef.current
    const s = getChatStrings(lang)

    // Save Property — saves and confirms
    if (toolName === 'save_property') {
      if (propertySaved) {
        setMessages((prev) => prev.map((m) => m.id === toolActionMsg.id ? { ...m, applied: true } : m))
        setMessages((prev) => [...prev, {
          id: Date.now() + '_confirm_save',
          role: 'assistant',
          content: s.alreadySaved,
        }])
        return
      }
      onSaveProperty?.()
      setMessages((prev) => prev.map((m) => m.id === toolActionMsg.id ? { ...m, applied: true } : m))
      setMessages((prev) => [...prev, {
        id: Date.now() + '_confirm_save',
        role: 'assistant',
        content: s.propertySaved,
      }])
      return
    }

    // Skip Trace — auto-save first if not saved, then run skip trace
    if (toolName === 'skip_trace_property') {
      setMessages((prev) => prev.map((m) => m.id === toolActionMsg.id ? { ...m, applied: true } : m))
      // The trace's callbacks can arrive after the user has moved to another property (the modal's
      // request isn't aborted on close), so each one is dropped once the chat session has changed.
      const traceSession = sessionIdRef.current
      const forThisProperty = (fn) => (...args) => {
        if (sessionIdRef.current === traceSession) fn(...args)
      }
      // Runs only once the trace has really started (the Skip Trace modal submitted it), so the chat
      // never says it started when it didn't (no credits, a trace already in progress, modal cancelled…).
      const onStarted = forThisProperty(() => {
        skipTraceWatchRef.current = { lastSkipTraceDate: propertyRef.current?.get('lastSkipTraceDate') ?? null }
        setMessages((prev) => [...prev, {
          id: Date.now() + '_confirm_st',
          role: 'assistant',
          content: s.skipTraceInitiated,
        }])
      })
      // Runs when the trace didn't complete or its results couldn't be loaded (`status` as in
      // Detail.handleSkipTrace's onFailed). The Skip Trace modal shows its own toast as well.
      const onFailed = forThisProperty((status) => {
        if (status === 'DUPLICATE') {
          // Traced in the last 6 months: no new search ran, but the contact was just reloaded with the
          // cached results. This runs before that reload reaches a render, so let the effect on
          // `property` show them on that render (as onFinished does).
          skipTraceWatchRef.current = { finished: true, duplicate: true }
          return
        }
        skipTraceWatchRef.current = null
        let content = s.skipTraceFailed
        if (status === 'NO_MATCH') {
          content = s.skipTraceNoResults
        } else if (status === 'RELOAD_ERROR') {
          content = s.skipTraceResultsNotLoaded
        }
        setMessages((prev) => [...prev, {
          id: Date.now() + '_skip_trace_outcome',
          role: 'assistant',
          content,
        }])
      })
      // Runs once the contact has been reloaded after the trace. It runs before that reload reaches a
      // render, so it only marks the watch; the effect on `property` prints the result on that render.
      const onFinished = forThisProperty(() => {
        if (skipTraceWatchRef.current) skipTraceWatchRef.current.finished = true
      })
      const startSkipTrace = forThisProperty(() => onSkipTrace?.({ onStarted, onFinished, onFailed }))
      if (propertySaved) {
        startSkipTrace()
      } else {
        // The save's "Skip Trace This Property" checkbox may already have started the trace;
        // otherwise start it once the saved property and its contact have reached the chat.
        onSaveProperty?.(forThisProperty(({ skipTraceStarted, skipTraceFinished, skipTraceFailed }) => {
          if (skipTraceFailed) onFailed(skipTraceFailed)
          else if (skipTraceFinished) onFinished()
          else if (skipTraceStarted) onStarted()
          else pendingSkipTraceRef.current = startSkipTrace
        }))
      }
      return
    }

    const calcLabel = s.calcLabels[toolName] || s.calcLabels.update_fix_flip
    const ref = toolName === 'update_rental_roi' ? rentalRef : flipRef

    if (!ref?.current?.setInputs) {
      // Ask permission before opening
      setMessages((prev) => [...prev, {
        id: Date.now() + '_confirm_open',
        role: 'open_prompt',
        lang,
        calcLabel,
        toolActionMsg,
      }])
      return
    }

    ref.current.setInputs(updates)
    setMessages((prev) => prev.map((m) => m.id === toolActionMsg.id ? { ...m, applied: true } : m))
    setMessages((prev) => [...prev, {
      id: Date.now() + '_confirm',
      role: 'assistant',
      content: s.calcUpdated(calcLabel),
    }])
  }

  function handleConfirmOpen(openPromptMsg) {
    const { toolActionMsg } = openPromptMsg
    const s = getChatStrings(openPromptMsg.lang || langRef.current)
    const tab = toolActionMsg.toolName === 'update_rental_roi' ? 'rental' : 'flip'
    setPendingToolAction(toolActionMsg)
    onOpenCalculators?.(tab)
    setMessages((prev) => prev.filter((m) => m.id !== openPromptMsg.id))
    setMessages((prev) => [...prev, {
      id: Date.now() + '_opening',
      role: 'assistant',
      content: s.openingCalculators,
    }])
  }

  function handleOpenComps(viewCompsMsg) {
    if (calculatorsOpen) onCloseCalculators?.()
    onOpenCompsTab?.()
    setMessages((prev) => prev.map((m) => m.id === viewCompsMsg.id ? { ...m, opened: true } : m))
  }

  // Open a calculator directly from a clickable ActionCard (Rental ROI / Fix & Flip)
  function handleOpenAction(actionMsg) {
    if (actionMsg.action === 'open_rental_roi_calculator') {
      onOpenCalculators?.('rental')
    } else if (actionMsg.action === 'open_fix_flip_calculator') {
      onOpenCalculators?.('flip')
    }
    setMessages((prev) => prev.map((m) => m.id === actionMsg.id ? { ...m, opened: true } : m))
  }

  async function handleSend(textToSend, { voice: isVoiceTurn = false } = {}) {
    const text = textToSend ?? inputText.trim();

    // Clear the live transcript bubble even if this spoken turn is dropped below
    if (isVoiceTurn) voice.clearTranscript()

    if (!text || loading) return;

    if (!textToSend) {
      setInputText('');
    }

    // A spoken turn keeps voice mode; any other send (typed, AI Summary) ends
    // it, so nothing is heard or read aloud for a turn the user didn't speak.
    if (!isVoiceTurn && voiceActive) stopVoice()
    const voiceGen = voiceGenRef.current

    const userMsg = { id: Date.now() + '_u', role: 'user', content: text === AI_SUMMARY_PROMPT ? DISPLAY_PROMPT : text }
    setMessages((prev) => [...prev, userMsg])

    const newHistory = [...history, { role: 'user', content: text }]

    // Track the conversation language from what the user actually typed (the
    // internal AI summary prompt is always English, so it doesn't count).
    if (text !== AI_SUMMARY_PROMPT) langRef.current = detectLang(text, langRef.current)
    const s = getChatStrings(langRef.current)

    // Auto-load comparables if question is about comps and data isn't loaded yet
    const compsAlreadyLoaded = (comparablesType?.get?.('properties')?.size ?? 0) > 0
    const looksLikeCompsQuestion = text !== AI_SUMMARY_PROMPT &&
  /comp(?:arable)?|nearby\s+(?:sale|listing|home|prop|sold)|similar\s+(?:prop|home|sale|sold)|neighbor(?:ing|hood)?\s+(?:sale|home|prop)|sold\s+nearby|(?:ventas?|propiedades|casas)\s+(?:cercanas|similares)/i.test(text)
    if (!compsAlreadyLoaded && looksLikeCompsQuestion && loadComparables && !pendingCompsQuestion) {
      loadComparables()
      setPendingCompsQuestion({ pendingHistory: newHistory, voiceTurn: isVoiceTurn, voiceGen, lang: langRef.current })
      setMessages((prev) => [...prev, {
        id: Date.now() + '_loading_comps',
        role: 'assistant',
        content: text === AI_SUMMARY_PROMPT ? s.creatingSummary : s.loadingComps,
      }])
      return
    }

    setLoading(true)

    const context = buildContext(property, rentalRef, flipRef, comparablesType, linkedProperties, isCompsTabActive, { propertySaved, skipTraceCreditsRemaining }, detailTabs)
    const streamMsgId = Date.now() + '_stream' + Math.random()
    let fullText = ''
    let renderedLength = 0
    let renderTimer = null
    let streamDone = false
    let firstToken = true
    let errorText = ''

    function startRenderLoop() {
      if (renderTimer) return
      renderTimer = setInterval(() => {
        if (renderedLength < fullText.length) {
          renderedLength = Math.min(renderedLength + STREAM_CHARS_PER_TICK, fullText.length)
          setMessages((prev) => prev.map((m) =>
            m.id === streamMsgId ? { ...m, content: fullText.slice(0, renderedLength) } : m
          ))
        } else if (streamDone) {
          clearInterval(renderTimer)
          if (renderTimerRef.current === renderTimer) renderTimerRef.current = null
          renderTimer = null
        }
      }, STREAM_RENDER_INTERVAL_MS)
      renderTimerRef.current = renderTimer
    }

    abortRef.current?.abort()
    abortRef.current = new AbortController()
    // Stale once this request is aborted (Stop, property change, newer request)
    // or the component unmounts — late callbacks must not touch the chat.
    const controller = abortRef.current
    const isStale = () => !mountedRef.current || controller.signal.aborted

    try {
      await callAnthropicStreaming(newHistory, context, sessionIdRef.current, {
        signal: abortRef.current.signal,
        onText: (text) => {
          if (isStale()) return
          if (firstToken) {
            firstToken = false
            setStreaming(true)
            setMessages((prev) => [...prev, {
              id: streamMsgId,
              role: 'assistant',
              content: '',
            }])
          }
          fullText += text
          startRenderLoop()
        },
        onToolUse: (toolName, args) => {
          if (isStale()) return
          if (toolName === 'open_comparables_tab') {
            if (!isCompsTabActive) {
              setMessages((prev) => [...prev, {
                id: Date.now() + '_vc' + Math.random(),
                role: 'view_comps',
                lang: langRef.current,
                opened: false,
              }])
            }
          } else if (
            toolName === 'open_rental_roi_calculator' ||
            toolName === 'open_fix_flip_calculator'
          ) {
            setMessages((prev) => [...prev, {
              id: Date.now() + '_ac' + Math.random(),
              role: 'action',
              lang: langRef.current,
              action: toolName,
              opened: false,
            }])
          } else {
            const toolActionMsg = {
              id: Date.now() + '_t' + Math.random(),
              role: 'tool_action',
              lang: langRef.current,
              toolName,
              updates: args || {},
              description: summarizeUpdates(toolName, args || {}, langRef.current),
              applied: false,
            }
            setMessages((prev) => [...prev, toolActionMsg])
          }
        },
        onDone: () => {},
        onError: (err) => {
          if (isStale()) return
          errorText = getChatStrings(langRef.current).error(err.message)
          setMessages((prev) => [...prev, {
            id: Date.now() + '_err',
            role: 'assistant',
            content: errorText,
          }])
        },
      })

      // Mark stream as done so the render loop finishes draining
      streamDone = true

      // Wait for render loop to finish displaying all text
      await new Promise((resolve) => {
        const check = setInterval(() => {
          if (renderedLength >= fullText.length || isStale()) {
            clearInterval(check)
            resolve()
          }
        }, STREAM_RENDER_INTERVAL_MS)
      })

      if (isStale()) return

      // Remove the placeholder if it ended up empty (tool-only response)
      if (!fullText) {
        setMessages((prev) => prev.filter((m) => m.id !== streamMsgId))
      }

      // Read a spoken question's answer aloud, once it has finished drawing
      if (isVoiceTurn) speakVoiceReply(fullText || errorText, voiceGen)

      // Update history with the final assistant text
      const updatedHistory = fullText
        ? [...newHistory, { role: 'assistant', content: fullText }]
        : [...newHistory]
      setHistory(updatedHistory)
    } catch (err) {
      streamDone = true
      if (fullText) {
        await new Promise((resolve) => {
          const check = setInterval(() => {
            if (renderedLength >= fullText.length || isStale()) {
              clearInterval(check)
              resolve()
            }
          }, STREAM_RENDER_INTERVAL_MS)
        })
      }
      if (renderTimer) { clearInterval(renderTimer); renderTimer = null }
      // Stop / unmount aborts are intentional — leave partial text, no error bubble.
      if (isStale() || err.name === 'AbortError') return
      // Remove empty placeholder on error
      setMessages((prev) => prev.filter((m) => m.id === streamMsgId && !m.content ? false : true))
      setMessages((prev) => [...prev, {
        id: Date.now() + '_err',
        role: 'assistant',
        content: getChatStrings(langRef.current).error(err.message),
      }])
      if (isVoiceTurn) speakVoiceReply(getChatStrings(langRef.current).error(err.message), voiceGen)
    } finally {
      if (!isStale()) {
        setLoading(false)
        setStreaming(false)
      }
    }
  }
  handleSendRef.current = handleSend

  // Cancel the in-flight stream from the UI. The aborted request resolves
  // silently; loading/streaming are reset here so the input re-enables at once.
  function handleStop() {
    abortRef.current?.abort()
    if (renderTimerRef.current) {
      clearInterval(renderTimerRef.current)
      renderTimerRef.current = null
    }
    setLoading(false)
    setStreaming(false)
  }

  function handlePrintChat() {
    const address = formatFullAddressObject(property.get('address'), true) || 'Property'
    const chatMessages = messages.filter((m) => m.role === 'user' || m.role === 'assistant')
    if (chatMessages.length === 0) return

    const messagesHTML = chatMessages.map((m) => {
      const isUser = m.role === 'user'
      const label = isUser ? 'You' : 'PropStream Intelligence'
      const labelColor = isUser ? '#1B3A6B' : '#5B68C3'
      const bgColor = isUser ? '#1B3A6B' : '#fff'
      const textColor = isUser ? '#fff' : '#3C4654'
      const border = isUser ? 'none' : '1px solid #DFE1E4'
      // Convert markdown bold/italic to HTML
      const content = (m.content || '')
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/`(.+?)`/g, '<code style="background:#f3f5f8;border:1px solid #d0d2d5;border-radius:4px;padding:1px 5px;font-size:0.9em">$1</code>')
        .replace(/^### (.+)$/gm, '<div style="font-size:14px;font-weight:700;color:#3C4654;margin:8px 0 4px">$1</div>')
        .replace(/^## (.+)$/gm, '<div style="font-size:15px;font-weight:700;color:#3C4654;margin:10px 0 4px">$1</div>')
        .replace(/^[-*] (.+)$/gm, '<div style="display:flex;gap:6px;margin-bottom:2px"><span style="color:#5B68C3;font-weight:700">•</span><span>$1</span></div>')
        .replace(/\n/g, '<br/>')

      return `<div style="margin-bottom:12px">
        <div style="font-size:11px;font-weight:600;color:${labelColor};margin-bottom:4px">${label}</div>
        <div style="background:${bgColor};color:${textColor};border:${border};border-radius:12px;padding:10px 14px;font-size:13px;line-height:1.6;${isUser ? 'white-space:pre-wrap' : ''}">${content}</div>
      </div>`
    }).join('')

    const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<title>PropStream Intelligence – Chat Transcript</title>
<link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap" rel="stylesheet"/>
<style>
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
body{font-family:Inter,system-ui,sans-serif;font-size:13px;color:#3C4654;padding:24px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.header{margin-bottom:16px;border-bottom:2px solid #5B68C3;padding-bottom:12px}
.header h1{font-size:22px;font-weight:700;color:#3C4654;margin:0}
.header p{font-size:14px;color:#66707D;margin:2px 0 0}
.messages{max-width:700px}
.timestamp{margin-top:20px;font-size:11px;color:#66707D;text-align:center;border-top:1px solid #DFE1E4;padding-top:8px}
@media print{body{padding:12px}@page{margin:0.5in;size:auto}}
</style>
</head>
<body>
<div class="header">
  <h1>PropStream Intelligence</h1>
  <p>Chat Transcript – ${address}</p>
</div>
<div class="messages">${messagesHTML}</div>
<div class="timestamp">Generated on ${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} at ${new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</div>
</body>
</html>`

    const win = window.open('', '_blank')
    if (!win) return
    win.document.write(html)
    win.document.close()
    win.onload = () => {
      win.addEventListener('afterprint', () => win.close())
      win.matchMedia('print').addEventListener('change', (e) => {
        if (!e.matches) setTimeout(() => win.close(), 300)
      })
      win.print()
    }
  }

  // ── Always-visible vertical tab + conditional chat panel ─────────────────────
  return (
    <>
      <style>{CHAT_KEYFRAMES}</style>
      {/* ── Floating pill button (always visible) ── */}
      <button
        onClick={() => {
          if (!open) { setPanelPos(DEFAULT_POS); setPanelSize(DEFAULT_SIZE); }
          setOpen(!open);
        }}
        style={{
          all: 'unset',
          boxSizing: 'border-box',
          position: 'fixed',
          bottom: 112,
          right: 20,
          zIndex: 10001,
          backgroundColor: '#5B68C3',
          border: 'none',
          borderRadius: 999,
          padding: '9px 14px',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 1,
          height: 38,
          maxWidth: 164,
          fontFamily: 'Inter, sans-serif',
          boxShadow: '0px 4px 6px 0px rgba(0, 0, 0, 0.05), 0px 10px 15px 0px rgba(0, 0, 0, 0.1)',
        }}
      >
        {/* Star icon + text group */}
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 3 }}>
          {/* Animated star */}
          <svg 
            xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none"
            style={{ animation: 'psStarSpin 2s ease-in-out infinite', flexShrink: 0 }}

          >
            <path d="M9.71757 0.555288L12.0974 5.67187L17.214 8.05168C17.5313 8.21033 17.7692 8.52762 17.7692 8.88462C17.7692 9.28129 17.5313 9.5986 17.214 9.75717L12.0974 12.1371L9.71757 17.2536C9.55892 17.5709 9.24161 17.7692 8.88462 17.7692C8.48794 17.7692 8.17067 17.5709 8.01202 17.2536L5.63221 12.1371L0.515625 9.75717C0.198317 9.5986 0 9.28129 0 8.88462C0 8.52762 0.198317 8.21033 0.515625 8.05168L5.63221 5.67187L8.01202 0.555288C8.17067 0.237981 8.48794 0 8.88462 0C9.24161 0 9.55892 0.237981 9.71757 0.555288Z" fill="white"/>
          </svg>
          {/* Two-line text */}
          <div style={{ display: 'flex', flexDirection: 'column', marginTop: -2 }}>
            <span style={{ fontSize: 11, fontWeight: 600, lineHeight: '13px', color: '#fff', whiteSpace: 'nowrap' }}>PropStream</span>
            <span style={{ fontSize: 16, fontWeight: 600, lineHeight: '15px', color: '#fff', whiteSpace: 'nowrap' }}>Intelligence</span>
          </div>
        </div>
        {/* Caret */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingLeft: 8, flexShrink: 0 }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="9" viewBox="0 0 15 9" fill="none" style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>
            <path d="M6.80419 0.287393C7.18739 -0.0957978 7.80701 -0.0957978 8.18613 0.287393L14.7126 6.8098C15.0958 7.19299 15.0958 7.81262 14.7126 8.19173C14.3294 8.57085 13.7098 8.57492 13.3307 8.19173L7.50128 2.36233L1.6678 8.19581C1.28461 8.579 0.664981 8.579 0.285867 8.19581C-0.0932479 7.81262 -0.0973244 7.19299 0.285867 6.81387L6.80419 0.287393Z" fill="#E8EBFC"/>
          </svg>
        </div>
      </button>

      {/* ── Chat panel (toggled) ── */}
      {open &&
      <div style={{
        position: 'fixed', bottom: panelPos.bottom, right: panelPos.right, zIndex: 10001,
        width: panelSize.width, height: panelSize.height,
        display: 'flex', flexDirection: 'column',
        borderRadius: 14, overflow: 'hidden',
        boxShadow: '0px 10px 10px 0px rgba(0, 0, 0, 0.04), 0px 20px 25px 0px rgba(0, 0, 0, 0.1)',
        fontFamily: 'Inter, sans-serif',
        border: '1px solid rgba(27,58,107,0.15)',
      }}>
        {/* ── Resize handle (top-left corner) ── */}
        <div
          onMouseDown={handleResizeStart}
          style={{
            position: 'absolute', top: 4, left: 4, width: 20, height: 20,
            cursor: 'nw-resize', zIndex: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
            <circle cx="1.5" cy="1.5" r="1.5" fill="#AFB6E5"/>
            <circle cx="1.5" cy="6.5" r="1.5" fill="#AFB6E5"/>
            <circle cx="1.5" cy="11.5" r="1.5" fill="#AFB6E5"/>
            <circle cx="6.5" cy="1.5" r="1.5" fill="#AFB6E5"/>
            <circle cx="6.5" cy="6.5" r="1.5" fill="#AFB6E5"/>
            <circle cx="11.5" cy="1.5" r="1.5" fill="#AFB6E5"/>
          </svg>
        </div>

        {/* ── Header (draggable) ── */}
        <div
          onMouseDown={handleDragStart}
          style={{
            boxSizing: 'border-box',
            background: '#5B68C3',
            padding: '9px 16px 9px 30px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexShrink: 0,
            borderRadius: '12px 12px 0 0',
            boxShadow: '0px 4px 6px 0px rgba(0, 0, 0, 0.05), 0px 10px 15px 0px rgba(0, 0, 0, 0.1)',
            cursor: 'grab',
            userSelect: 'none',
            height: 52,
          }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {/* Drag grip indicator */}
            <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 3, opacity: 0.6 }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="23" height="14" viewBox="0 0 23 14" fill="none">
                <circle cx="2.5" cy="2.5" r="2.5" fill="#AFB6E5"/>
                <circle cx="11.5" cy="2.5" r="2.5" fill="#AFB6E5"/>
                <circle cx="20.5" cy="2.5" r="2.5" fill="#AFB6E5"/>
                <circle cx="2.5" cy="11.5" r="2.5" fill="#AFB6E5"/>
                <circle cx="11.5" cy="11.5" r="2.5" fill="#AFB6E5"/>
                <circle cx="20.5" cy="11.5" r="2.5" fill="#AFB6E5"/>
              </svg>
            </div>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 3 }}>
              <svg width="21" height="21" viewBox="0 0 21 21" fill="none" style={{ flexShrink: 0 }}>
                <path d="M11.4844 0.656051L14.2969 6.70293L20.3438 9.51543C20.7188 9.70293 21 10.0779 21 10.4998C21 10.9686 20.7188 11.3436 20.3438 11.531L14.2969 14.3436L11.4844 20.3904C11.2969 20.7654 10.9219 20.9998 10.5 20.9998C10.0312 20.9998 9.65625 20.7654 9.46875 20.3904L6.65625 14.3436L0.609375 11.531C0.234375 11.3436 0 10.9686 0 10.4998C0 10.0779 0.234375 9.70293 0.609375 9.51543L6.65625 6.70293L9.46875 0.656051C9.65625 0.281051 10.0312 -0.000199318 10.5 -0.000199318C10.9219 -0.000199318 11.2969 0.281051 11.4844 0.656051Z" fill="white"/>
              </svg>
              <div style={{ display: 'flex', flexDirection: 'column', marginTop: -2 }}>
                <span style={{ fontSize: 14, fontWeight: 600, lineHeight: '15.8px', color: '#fff', whiteSpace: 'nowrap' }}>PropStream</span>
                <span style={{ fontSize: 20, fontWeight: 600, lineHeight: '20px', color: '#fff', marginTop: 0, whiteSpace: 'nowrap' }}>Intelligence</span>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 40 }}>
            {/* Print button */}
            <button
              onClick={handlePrintChat}
              title="Print Chat"
              style={{
                all: 'unset', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 30, height: 30, borderRadius: 6,
                color: 'rgba(255,255,255,0.7)',
                transition: 'color 0.15s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#fff'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255,255,255,0.7)'}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M4 4C4 2.89688 4.89687 2 6 2H12.6719C13.2031 2 13.7125 2.20937 14.0875 2.58437L15.4156 3.9125C15.7906 4.2875 16 4.79688 16 5.32812V6.5H4V4ZM2 10C2 8.89687 2.89688 8 4 8H16C17.1031 8 18 8.89687 18 10V13C18 13.5531 17.5531 14 17 14H16V16C16 17.1031 15.1031 18 14 18H6C4.89687 18 4 17.1031 4 16V14H3C2.44687 14 2 13.5531 2 13V10ZM6 15V16H14V13H6V15ZM16.25 10.5C16.25 10.0844 15.9156 9.75 15.5 9.75C15.0844 9.75 14.75 10.0844 14.75 10.5C14.75 10.9156 15.0844 11.25 15.5 11.25C15.9156 11.25 16.25 10.9156 16.25 10.5Z" fill="#E8EBFC"/>
              </svg>
            </button>
            {/* Caret to close */}
            <button
              onClick={() => setOpen(false)}
              style={{
                all: 'unset', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                paddingLeft: 0, height: 30,
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="9" viewBox="0 0 15 9" fill="none" style={{ transform: !open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>
                <path d="M8.19581 8.196C7.81261 8.5792 7.19299 8.5792 6.81387 8.196L0.287393 1.6736C-0.0957979 1.29041 -0.0957978 0.670781 0.287393 0.291667C0.670584 -0.0874484 1.29021 -0.0915244 1.66933 0.291667L7.49872 6.12107L13.3322 0.287591C13.7154 -0.0956002 14.335 -0.0956002 14.7141 0.287591C15.0932 0.670782 15.0973 1.29041 14.7141 1.66952L8.19581 8.196Z" fill="#E8EBFC"/>
              </svg>
            </button>
          </div>
        </div>

        {/* ── Message list ── */}
        <div style={{ flex: 1, minHeight: 0, overflowX: 'auto', overflowY: 'auto', background: '#EFF2F6', display: 'flex', flexDirection: 'column' }}>
          <div style={{ flex: 1, padding: '18px 12px' }}>
            {messages.length === 0 && (
              <div style={{ textAlign: 'center', color: '#66707D', fontSize: 13, marginTop: 48, padding: '0 16px' }}>
                <div style={{ marginBottom: 10 }}>
                  <BotAvatar />
                </div>
                <div style={{ fontWeight: 600, color: '#5B68C3', fontSize: 13, marginBottom: 4 }}>PropStream Intelligence</div>
                Ask anything about this property — financing, rental returns, flip scenarios, and more.
              </div>
            )}
            {messages.map((msg) => (
              <MessageItem key={msg.id} msg={msg} onApply={handleApplyTool} onConfirmOpen={handleConfirmOpen} onOpenComps={handleOpenComps} onOpenAction={handleOpenAction} />
            ))}
            {voice.voiceTranscript && (
              <div style={{ opacity: 0.9 }}>
                <MessageItem msg={{ id: 'voice-transcript', role: 'user', content: voice.voiceTranscript }} />
              </div>
            )}
            {loading && !streaming && <LoadingDots />}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* ── Voice error line (same copy as AI Search) ── */}
        {voice.voiceError && (
          <div role="alert" aria-live="assertive" style={{ background: '#fff', borderTop: '1px solid #CED1D6', padding: '8px 12px 0', fontSize: 11, color: '#e5484d', fontFamily: 'Inter, sans-serif' }}>
            {voice.voiceError}
          </div>
        )}

        {/* ── Input bar ── */}
        <div style={{
          background: '#fff',
          borderTop: voice.voiceError ? 'none' : '1px solid #CED1D6',
          padding: '10px 12px',
          display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
        }}>
          {/* In voice mode the box stays on screen but can't be typed in (as in AI Search) */}
          <div style={{ position: 'relative', flex: 1, minWidth: 0, display: 'flex' }}>
            <input
              ref={inputRef}
              value={voiceActive ? '' : inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() } }}
              placeholder={voiceActive ? '' : 'Ask about this property...'}
              disabled={loading || voiceActive}
              style={{
                all: 'unset',
                boxSizing: 'border-box',
                flex: 1, minWidth: 0, border: '1px solid #5B68C3', borderRadius: 24,
                padding: '9.5px 14.5px', fontSize: 13,
                fontFamily: 'Inter, sans-serif',
                background: loading || voiceActive ? '#f8fafc' : '#fff',
                color: '#3C4654',
              }}
            />
            {voiceActive && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', padding: '0 15px', pointerEvents: 'none' }}>
                <VoiceStatus voice={voice} busy={voiceBusy} />
              </div>
            )}
          </div>
          <VoiceControls voice={voice} busy={voiceBusy} onEnd={handleVoiceEnd} />
          {!voiceActive && (loading ? (
            <button
              onClick={handleStop}
              aria-label="Stop generating"
              style={{
                all: 'unset',
                boxSizing: 'border-box',
                background: '#5B68C3',
                color: '#fff', borderRadius: 24,
                padding: '10.25px 16px',
                cursor: 'pointer',
                fontWeight: 700, fontSize: 13, flexShrink: 0,
                fontFamily: 'Inter, sans-serif',
                lineHeight: '15px',
                transition: 'background 0.15s',
                display: 'flex', alignItems: 'center', gap: 5,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#4A57B2' }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#5B68C3' }}
            >
              Stop
              <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true">
                <rect x="0.5" y="0.5" width="10" height="10" rx="2" fill="white"/>
              </svg>
            </button>
          ) : (
            <button
              onClick={(e) => {
                e.preventDefault();
                handleSend();
              }}
              disabled={!inputText.trim()}
              style={{
                all: 'unset',
                boxSizing: 'border-box',
                background: !inputText.trim() ? '#a3a8ad' : '#5B68C3',
                color: '#fff', borderRadius: 24,
                padding: '10.25px 16px',
                cursor: !inputText.trim() ? 'default' : 'pointer',
                fontWeight: 700, fontSize: 13, flexShrink: 0,
                fontFamily: 'Inter, sans-serif',
                lineHeight: '15px',
                transition: 'background 0.15s',
                display: 'flex', alignItems: 'center', gap: 5,
              }}
              onMouseEnter={(e) => { if (inputText.trim()) e.currentTarget.style.background = '#4A57B2' }}
              onMouseLeave={(e) => { if (inputText.trim()) e.currentTarget.style.background = '#5B68C3' }}
            >
              Send
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                <path d="M11.9166 1.0835L5.95825 7.04183M11.9166 1.0835L8.12492 11.9168L5.95825 7.04183M11.9166 1.0835L1.08325 4.87516L5.95825 7.04183" stroke="white" strokeWidth="1.35417" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          ))}
        </div>
      </div>}

      {voice.micPermissionBlocked && (
        <MicPermissionModal
          onDismiss={voice.dismissMicPermission}
          onRetry={voice.retryMicPermission}
          zIndex={10002}
        />
      )}
      {!voice.micPermissionBlocked && voice.voiceNetworkError && (
        <MicPermissionModal
          title={VOICE_NETWORK_ERROR_TITLE}
          body={null}
          onDismiss={voice.dismissNetworkError}
          onRetry={voice.retryNetworkError}
          zIndex={10002}
        />
      )}
    </>
  )
}
